// src/components/auth/SocialLoginButtons.tsx
// Container component for all social login buttons

import React, { useCallback } from 'react';
import { Box, Alert } from '@mui/material';
import { OAuthProviders, isOAuthProviderAvailable } from '../../config/oauth';
import { OAuthError } from '../../types/oauth';
import { User } from '../../services/authService';
import useAuthStore from '../../store/authStore';
import GoogleSignInButton from './GoogleSignInButton';
import MicrosoftSignInButton from './MicrosoftSignInButton';
import SocialLoginDivider from './SocialLoginDivider';

interface SocialLoginButtonsProps {
  /** Callback when any OAuth login succeeds */
  onSuccess: (user: User, isNewUser?: boolean) => void;
  /** Callback when any OAuth login fails */
  onError: (error: OAuthError) => void;
  /** Whether all buttons should be disabled */
  disabled?: boolean;
  /** Context: signin or signup page */
  context?: 'signin' | 'signup';
  /** Whether to show the divider above buttons */
  showDivider?: boolean;
  /** Custom divider text */
  dividerText?: string;
  /** Error message to display */
  error?: string | null;
}

/**
 * Container component that renders all available social login buttons
 * Automatically shows only enabled and configured providers
 */
const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  onSuccess,
  onError,
  disabled = false,
  context = 'signin',
  showDivider = true,
  dividerText,
  error,
}) => {
  // Check which providers are available
  const isGoogleAvailable = isOAuthProviderAvailable(OAuthProviders.GOOGLE);
  const isMicrosoftAvailable = isOAuthProviderAvailable(OAuthProviders.MICROSOFT);

  // The provider buttons talk to oauthService directly, which stores the JWT
  // but never told the auth store who signed in. Route guards read the store,
  // so a brand-new Google user was bounced from /dashboard back to /login.
  // Publish the user here, before any navigation happens.
  const handleSuccess = useCallback(
    (user: User, isNewUser?: boolean) => {
      useAuthStore.getState().setUser(user);
      onSuccess(user, isNewUser);
    },
    [onSuccess],
  );

  // If no providers are available, don't render anything
  const hasAnyProvider = isGoogleAvailable || isMicrosoftAvailable;
  if (!hasAnyProvider) {
    return null;
  }

  return (
    <Box>
      {showDivider && <SocialLoginDivider text={dividerText} />}

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Social login buttons container */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Google Sign-In Button */}
        {isGoogleAvailable && (
          <GoogleSignInButton
            onSuccess={handleSuccess}
            onError={onError}
            disabled={disabled}
            context={context}
            fullWidth
          />
        )}

        {/* Microsoft Sign-In Button */}
        {isMicrosoftAvailable && (
          <MicrosoftSignInButton
            onSuccess={handleSuccess}
            onError={onError}
            disabled={disabled}
            context={context}
            fullWidth
          />
        )}
      </Box>
    </Box>
  );
};

export default SocialLoginButtons;
